import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { createSelector, select, Store } from "@ngrx/store";
import { environment } from "../../../../environments/environment";

import { DocumentType, MsgTypeForGenerator } from "@helper/abstraction/documents";
import { ValidatorsUtil } from "@helper/validators-util";
import { Role } from "@helper/abstraction/roles";
import { notNull } from "@helper/operators";
import { DefaultPopupComponent } from "@shared/overlay/default-pop-up.component";
import { exportXLSXDocuments } from "@app/user/user.actions";
import { UserState } from "@app/user/user.reducer";

const OutputTypeValues = [
	['PDF', 'PDF'],
	['XLSX', 'XLSX'],
];

@Component({
	selector: "app-printing-popup",
	templateUrl: "./printing-popup.component.html",
	styleUrls: ["./printing-popup.component.scss"]
})
export class PrintingPopupComponent extends DefaultPopupComponent implements OnDestroy, OnInit {
	@Input() public documentType?: DocumentType;
	@Input() public ids?: number[];
	@Input() public formType?: number;
	@Input() public isMassAction = false;
	@Input() public isStatistic = false;

	public form: FormGroup;
	public env = environment;
	public outputTypeValues = OutputTypeValues;
	public usedSignApi = false;
	public viewCheckBox = false;
	public allowedDocumentTypes: (DocumentType | MsgTypeForGenerator)[] = ["EWAYBILL", "EINVOICE", "EACTDIF", "ROSEU", "UKD_PR", "UPD_PR"];
	private printSignRole?: boolean;
	private unsubscribe$$: Subject<void> = new Subject<void>();

	constructor(
		private fB: FormBuilder,
		private store: Store
	) {
		super();
		this.form = this.fB.group({
			outputType: [null, Validators.required],
			shortForm: false,
			printEds: false
		});

		const selectUser = (appState: any): UserState => appState.user;
		const selectUserRoles = createSelector(selectUser, (state: UserState): Role[] | undefined => state.roles);
		this.store.pipe(
			select(selectUserRoles),
			notNull(),
			takeUntil(this.unsubscribe$$),
		).subscribe(roles => this.printSignRole = roles.some(role => role.id === "PRINT_SIGN"));
	}

	public ngOnDestroy(): void {
		this.outputTypeValues[0][0] = $localize`PDF`;
		this.unsubscribe$$.next();
		this.unsubscribe$$.complete();
	}

	public ngOnInit(): void {
		const format = localStorage.getItem("format");
		const printEds = JSON.parse(localStorage.getItem("printEds")!);
		this.checkApi();
		this.form.patchValue({
			outputType: format || "XLSX",
			printEds: (printEds === null || printEds) && this.viewCheckBox && !this.usedSignApi ? true : false
		});
	}

	public printData(): void {
		if (this.form.invalid) {
			ValidatorsUtil.triggerValidation(this.form);
			return;
		}
		if (!this.ids) {
			throw "No documents' IDs to print the information";
		}
		if (!this.documentType) {
			throw "No documents' type to print the information";
		}
		localStorage.setItem("format", this.form.value.outputType);
		localStorage.setItem("printEds", this.form.value.printEds);
		const action = exportXLSXDocuments({
			documentType: this.documentType,
			documentIds: this.ids,
			exportProps: {
				exportFormatType: this.form.value.outputType,
				isMassAction: this.isMassAction,
				printEds: this.form.value.printEds,
				shortForm: !this.form.value.shortForm,
				stampUnitedly: this.usedSignApi,
			}
		});
		this.store.dispatch(action);
		this.close();
	}

	public toggleControlValue(controlName: string): void {
		this.form.get(controlName)?.setValue(!this.form.value[controlName]);
	}

	public getOutputTypeValues(): string[][] {
		if ((this.usedSignApi && !["BM"].includes(this.env.project)) || (["UPD_PR"].includes(this.documentType!) && ["BM"].includes(this.env.project))) {
			this.outputTypeValues[0][0] = $localize`PDF (со штампом ЭЦП)`;
		}
		return this.outputTypeValues;
	}

	private checkApi(): void {
		const isAllowDt = this.allowedDocumentTypes.includes(this.documentType!);
		switch (this.env.project) {
			case "CTT": {
				if (this.isStatistic && ["EWAYBILL"].includes(this.documentType!)) {
					this.usedSignApi = true;
				}
				if (this.isMassAction && ["EWAYBILL", "EINVOICE", "EACTDIF"].includes(this.documentType!)) {
					this.usedSignApi = true;
				}
				if (!this.isStatistic && !this.isMassAction && isAllowDt) {
					this.usedSignApi = true;
				}
			}
				break;
			case "ME": {
				if (["EWAYBILL", "EINVOICE", "EACTDIF"].includes(this.documentType!)) {
					this.usedSignApi = true;
				}
			}
				break;
			case "KZ": {
				if (isAllowDt) {
					this.usedSignApi = true;
				}
			}
				break;
			case "BM": {
				if (this.printSignRole && ["EWAYBILL", "EINVOICE"].includes(this.documentType!)) {
					this.viewCheckBox = true;
					this.usedSignApi = false;
				}
				if (["UPD_PR"].includes(this.documentType!)) {
					this.viewCheckBox = false;
					this.usedSignApi = true;
				}
			}
				break;
			default:
				this.usedSignApi = false;
				break;
		}
	}
}
